<template>
  <!-- Inline mode: show all apps as individual buttons with separators -->
  <template v-if="shouldShowPopupApps && inline && popupApps.length > 0">
    <div class="w-px h-8 bg-gray-200 mx-2 self-center" />

    <template v-for="(app, index) in popupApps" :key="app.id">
      <!-- App button with border separator -->
      <CButton
        v-tooltip="app.app_metadata?.display_name || app.name"
        :circle="isCircle"
        :class="[
          buttonClass,
          {
            'ml-1': index > 0,
          },
        ]"
        secondary
        :size="buttonSize"
        strong
        @click="onAppClick(app)"
      >
        <template #icon>
          <CIcon
            :fa-type="getAppIcon(app).includes('fa-kit') ? 'fa-kit' : 'far'"
            :icon="getAppIcon(app)"
            :size="iconSize"
          />
        </template>
      </CButton>
    </template>
  </template>

  <!-- Popover mode: group multiple apps in a popover -->
  <template v-else-if="shouldShowPopupApps && !inline">
    <!-- Apps button for multiple apps -->
    <CPopover
      v-if="hasMultipleApps"
      :placement="placement"
      :showArrow="true"
      :theme-overrides="{
        color: themeVars.base,
      }"
      to="body"
      trigger="click"
    >
      <template #trigger>
        <CButton
          v-tooltip="t('canvasUI.canvasBuilder.components.categories.apps')"
          :circle="isCircle"
          :class="buttonClass"
          secondary
          :size="buttonSize"
          strong
        >
          <template #icon>
            <CIcon fa-type="fas" icon="ellipsis" :size="iconSize" />
          </template>
        </CButton>
      </template>

      <AppGrid :apps="popupApps" :get-app-icon="getAppIcon" @click="onAppClick" />
    </CPopover>

    <!-- Single app button - direct access -->
    <CButton
      v-else-if="hasSingleApp && singleApp"
      v-tooltip="singleApp.app_metadata?.display_name || singleApp.name"
      :circle="isCircle"
      :class="buttonClass"
      secondary
      :size="buttonSize"
      strong
      @click="onAppClick(singleApp)"
    >
      <template #icon>
        <CIcon
          :fa-type="getAppIcon(singleApp).includes('fa-kit') ? 'fa-kit' : 'far'"
          :icon="getAppIcon(singleApp)"
          :size="iconSize"
        />
      </template>
    </CButton>
  </template>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CPopover from '@lib/components/CPopover/CPopover.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import AppGrid from '@canvas-builder/components/container/ControlBar/AppGrid.vue'
import { usePopupApps } from '@canvas-builder/composables/usePopupApps'
import tooltipDirective from '@lib/directives/tooltip'

interface Props {
  placement?:
    | 'left'
    | 'right'
    | 'top'
    | 'bottom'
    | 'top-start'
    | 'top-end'
    | 'bottom-start'
    | 'bottom-end'
    | 'left-start'
    | 'left-end'
    | 'right-start'
    | 'right-end'
  buttonClass?: string
  buttonSize?: 'tiny' | 'small' | 'medium' | 'large'
  iconSize?: string | number
  isCircle?: boolean
  /** When true, shows apps as inline buttons with separators instead of popover grouping */
  inline?: boolean
}

withDefaults(defineProps<Props>(), {
  placement: 'left',
  buttonClass: 'h-11 w-11',
  buttonSize: 'medium',
  iconSize: '18',
  isCircle: false,
  inline: false,
})

const { t } = useI18n()
const themeVars = useThemeVars()
const vTooltip = tooltipDirective
const { popupApps, shouldShowPopupApps, hasMultipleApps, hasSingleApp, singleApp, onAppClick, getAppIcon } =
  usePopupApps()
</script>
